import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, {
  login as apiLogin,
  logout as apiLogout,
  getMe,
  setBearer,
  setCsrfMirror,
  clearAuthFallback,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAdminInfo = useCallback(async () => {
    try {
      const response = await getMe();
      setAdmin(response.data);
    } catch (_error) {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminInfo();
  }, [fetchAdminInfo]);

  const login = async (email, password) => {
    try {
      const response = await apiLogin(email, password);
      const { admin: adminData, access_token, csrf_token } = response.data;
      // Preview-environment fallback: mirror bearer + csrf into sessionStorage.
      // In production (working cookies), the cookie path takes precedence.
      if (access_token) setBearer(access_token);
      if (csrf_token) setCsrfMirror(csrf_token);
      setAdmin(adminData);
      toast.success(`Welcome back, ${adminData.email}!`);
      return true;
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      // Non-fatal: cookies are cleared below regardless.
      console.warn("AdminAuth: server logout failed", error);
    }
    clearAuthFallback();
    setAdmin(null);
    toast.info("Logged out successfully");
  };

  const value = {
    admin,
    loading,
    isAuthenticated: !!admin,
    isSuperAdmin: admin?.is_super_admin || false,
    login,
    logout,
    refresh: fetchAdminInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
